import { 
  collection, 
  query, 
  where, 
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * 📊 Activity Service for Dashboard Last Update
 * 
 * Provides real-time subscription to the most recent human-caused activity
 * across incidents, sites, and crisis reports for dashboard display.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - TopCon Dashboard Implementation
 */

/**
 * Subscribe to the last human update for dashboard display
 * @param {string} orgId - Organization ID
 * @param {function} callback - Callback function to receive updates
 * @param {function} onError - Error callback (optional)
 * @returns {function} Unsubscribe function
 */
export const subscribeLastHumanUpdate = (orgId, callback, onError = null) => {
  try {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    console.log('🕐 DEBUGGING: Setting up subscription for org:', orgId);

    // SIMPLIFIED: Just query c3_incident_logs first to debug
    const incidentLogsRef = collection(db, 'c3_incident_logs');
    const query1 = query(
      incidentLogsRef,
      where('orgId', '==', orgId),
      orderBy('timestamp', 'desc'),
      limit(10) // Get more to see what's actually there
    );

    console.log('🕐 DEBUGGING: Setting up c3_incident_logs subscription...');

    const unsubscribe = onSnapshot(query1, 
      (snapshot) => {
        console.log('🕐 DEBUGGING: c3_incident_logs snapshot received, size:', snapshot.size);
        
        if (snapshot.empty) {
          console.log('🕐 DEBUGGING: No documents in c3_incident_logs');
          callback(null);
          return;
        }

        // Log all documents to see what we have
        snapshot.docs.forEach((doc, index) => {
          const data = doc.data();
          console.log(`🕐 DEBUGGING: Doc ${index}:`, {
            id: doc.id,
            userId: data.userId,
            action: data.action,
            details: data.details,
            timestamp: data.timestamp,
            userFullName: data.userFullName
          });
        });

        // Find the latest NON-System activity
        let latestActivity = null;
        for (const doc of snapshot.docs) {
          const data = doc.data();
          if (data.userId !== 'System') {
            latestActivity = data;
            console.log('🕐 DEBUGGING: Found latest human activity:', latestActivity);
            break;
          }
        }

        if (latestActivity) {
          const result = parseActivityForDisplay(latestActivity);
          console.log('🕐 DEBUGGING: Parsed result:', result);
          callback(result);
        } else {
          console.log('🕐 DEBUGGING: No human activities found');
          callback(null);
        }
      },
      (error) => {
        console.error('🕐 DEBUGGING: Error in subscription:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;

  } catch (error) {
    console.error('🕐 DEBUGGING: Error setting up subscription:', error);
    if (onError) onError(error);
    return () => {};
  }
};

/**
 * Parse activity log entry into dashboard display format
 * @param {Object} activity - Activity log entry
 * @returns {Object} Parsed activity for display
 */
const parseActivityForDisplay = (activity) => {
  if (!activity) return null;

  // Handle different data structures from different collections
  const {
    action,          // c3_incident_logs
    type,           // c3_activity_logs, c3_auditLogs  
    details,
    userFullName,
    userName,       // c3_activity_logs sometimes uses this
    userId,
    timestamp,
    metadata = {},
    incidentId,
    _source
  } = activity;

  // Normalize action/type field
  const actionType = action || type;
  
  console.log(`🕐 Parsing activity from ${_source}:`, { actionType, details, userFullName, userName });

  // Determine entity type and build message
  let entityType = 'incident'; // Default
  let entityName = 'Unknown';
  let actionSummary = 'updated';
  let entityId = incidentId;

  // Parse different activity types
  if (actionType && details) {
    // Staff status activities (from c3_activity_logs)
    if (actionType === 'STAFF_STATUS_CHANGED' || actionType.includes('STAFF') || details.includes('Staff status')) {
      entityType = 'staff';
      
      // Extract staff member info from metadata or details
      if (metadata.staffId) {
        entityName = `Staff Member ${metadata.staffId}`;
      } else {
        const staffMatch = details.match(/Staff\s+([^\s]+)/i) || 
                          details.match(/staff\s+status\s+changed\s+to\s+(\w+)/i);
        if (staffMatch) {
          entityName = staffMatch[1];
        }
      }

      // Extract status change
      const statusMatch = details.match(/status\s+changed\s+to\s+(\w+)/i) ||
                         details.match(/changed\s+to\s+(\w+)/i);
      
      if (statusMatch) {
        actionSummary = `status changed to ${statusMatch[1].toUpperCase()}`;
      } else {
        actionSummary = 'status updated';
      }
    }
    // Site-related activities
    else if (actionType.includes('site') || actionType.includes('SITE') || details.includes('site') || details.includes('Site')) {
      entityType = 'site';
      
      // Extract site name from details
      const siteNameMatch = details.match(/site[:\s]+"?([^"]+)"?/i) || 
                           details.match(/"([^"]+)"\s+status/i) ||
                           details.match(/Site\s+([^\s]+)/i);
      
      if (siteNameMatch) {
        entityName = siteNameMatch[1].trim();
      }

      // Extract status change
      const statusMatch = details.match(/status\s+(?:changed\s+)?to\s+(\w+)/i) ||
                         details.match(/set\s+to\s+(\w+)/i);
      
      if (statusMatch) {
        actionSummary = `status changed to ${statusMatch[1].toUpperCase()}`;
      } else {
        actionSummary = 'status updated';
      }
    }
    // Incident-related activities
    else if (actionType.includes('incident') || actionType.includes('INCIDENT') || incidentId) {
      entityType = 'incident';
      
      // Extract incident title from metadata or details
      if (metadata.incidentTitle) {
        entityName = metadata.incidentTitle;
      } else {
        const titleMatch = details.match(/incident[:\s]+"?([^"]+)"?/i) ||
                          details.match(/"([^"]+)"\s+(?:updated|created|closed)/i);
        if (titleMatch) {
          entityName = titleMatch[1].trim();
        }
      }

      // Parse action type
      if (actionType.includes('created') || details.includes('created')) {
        actionSummary = 'created';
      } else if (actionType.includes('status') || details.includes('status')) {
        const statusMatch = details.match(/status\s+(?:changed\s+)?to\s+(\w+)/i);
        if (statusMatch) {
          actionSummary = `status changed to ${statusMatch[1]}`;
        } else {
          actionSummary = 'status updated';
        }
      } else if (actionType.includes('severity') || details.includes('severity')) {
        actionSummary = 'severity updated';
      } else {
        actionSummary = 'updated';
      }
    }
    // Crisis report activities
    else if (actionType.includes('crisis_report') || actionType.includes('report') || actionType.includes('REPORT') || details.includes('report')) {
      entityType = 'report';
      
      // Extract report reference
      const refMatch = details.match(/report[:\s]+"?([^"]+)"?/i) ||
                      details.match(/"([^"]+)"\s+(?:promoted|acknowledged|declined)/i);
      
      if (refMatch) {
        entityName = refMatch[1].trim();
      }

      // Parse report action based on action type
      if (actionType === 'crisis_report_promoted' || details.includes('promoted')) {
        actionSummary = 'promoted to incident';
      } else if (actionType === 'crisis_report_acknowledged' || details.includes('acknowledged')) {
        actionSummary = 'acknowledged';
      } else if (actionType === 'crisis_report_declined' || details.includes('declined')) {
        actionSummary = 'declined';
      } else {
        actionSummary = 'updated';
      }
    }
  }

  // Format timestamp
  const activityTime = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const timeAgo = formatTimeAgo(activityTime);

  // Build the 3-line format
  const line1 = `${entityType.toUpperCase()} "${entityName}" ${actionSummary}`.toUpperCase();
  const line2 = userFullName || userName || `User ${userId}` || 'Unknown User';
  const line3 = timeAgo;

  return {
    entityType,
    entityId,
    entityName,
    actionSummary,
    userName: userFullName || userName || `User ${userId}`,
    timestamp: activityTime,
    timeAgo,
    // 3-line display format
    line1,
    line2, 
    line3,
    // Raw data for navigation
    rawActivity: activity
  };
};

/**
 * Format relative time for display
 * @param {Date} timestamp - Activity timestamp
 * @returns {string} Formatted relative time
 */
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Unknown time';
  
  const now = new Date();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  
  // For older activities, show the date
  return timestamp.toLocaleDateString('en-AU', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

export default {
  subscribeLastHumanUpdate
};
